import moment from 'moment';
import {
  uniqueId,
  isEmpty,
  orderBy,
} from 'lodash';
import { getValidDate } from '@/utils/date-utils';
import { formatToCode } from '@/utils/moment-date-utils';
import { buildFornecedorCompra, buildTransportadoraCompra } from '@/utils/fornecedor-utils';
import {
  Moeda,
  OrigemCompra,
  FinalidadeCompra,
  SituacaoRequisicao,
} from 'px-business-components';
import {
  computeFrom,
  initialStateCamposErp,
} from '@/modules/carrinho/finalizacao/campos-erp/validate-campos-erp';
import {
  computeFromProduto,
  initialStateCamposProdutoErp,
} from '@/modules/produtos/campos-erp/helpers/validate-campos-erp';

const SITUACOES_INVALIDAS = [
  SituacaoRequisicao.keys.CANCELADA.key,
  SituacaoRequisicao.keys.FINALIZADA.key,
];
const TEMP_KEYS = ['$$id', '$$itemTabelaPreco'];

const checkIsProdutoValido = situacao => !SITUACOES_INVALIDAS.includes(situacao);

const initialStateFrete = () => ({
  tipoFrete: null,
  prazoEntrega: null,
  totalTransporte: null,
  totalTransporteConvertido: null,
});

const getEspecificacaoProduto = (produto) => {
  if (produto.especificacaoProduto) {
    return produto.especificacaoProduto;
  }
  if (produto.especificacaoProdutoOrigem) {
    return produto.especificacaoProdutoOrigem;
  }
  return produto.produto ? produto.produto.especificacao : '';
};

export const initialStateImpostos = () => ({
  tipoAplicacaoIcms: null,
  tipoAplicacaoIpi: null,
  icms: 0,
  ipi: 0,
  valorIcms: 0,
  valorIpi: 0,
  ipiValorConvertido: 0,
  icmsValorConvertido: 0,
});

export const initialStateValoresConvertidoProduto = () => ({
  total: 0,
  precoUnitario: 0,
  totalProdutos: 0,
});

export const initialStateValoresConvertido = () => ({
  total: 0,
  totalProdutos: 0,
});

export const mapValoresTabelaPreco = (produto, itemTabelaPreco, isAVista) => ({
  ...produto,
  // adicionamos o item da tabela no produto para facilitar
  // a troca de valores e computar outros campos
  $$itemTabelaPreco: itemTabelaPreco,
  precoUnitario: isAVista ? itemTabelaPreco.precoAVista : itemTabelaPreco.precoAPrazo,
  impostos: {
    ...produto.impostos,
    icms: itemTabelaPreco.icms,
    ipi: itemTabelaPreco.ipi,
  },
});

export const initialStateProduto = (tipoErp, data) => ({
  $$id: uniqueId(),
  produto: null,
  quantidade: null,
  arquivos: [],
  unidadeMedida: null,
  especificacaoProduto: null,
  observacaoInterna: null,
  observacaoExterna: null,
  centroCusto: data?.centroCusto ?? null,
  projeto: data?.projeto ?? null,
  precoUnitario: null,
  tipoNotaEntrada: data?.tipoNotaEntrada ?? null,
  cfop: data?.cfop ?? null,
  variacaoCfop: data?.variacaoCfop ?? null,
  cadastrosAdicionais: initialStateCamposProdutoErp(tipoErp),
  impostos: initialStateImpostos(),
  dataFaturamento: data?.dataFaturamento ?? formatToCode(moment()),
  dataPrevisaoEntrega: data?.dataPrevisaoEntrega ?? null,
  finalidadeCompra: data?.finalidadeCompra ?? FinalidadeCompra.keys.USO_CONSUMO.key,
  $$itemTabelaPreco: null,
  total: 0,
  totalProdutos: 0,
  valorMoedaConvertido: initialStateValoresConvertidoProduto(),
});

export const initialState = tipoErp => ({
  comprador: null,
  locaisEntrega: null,
  fornecedor: null,
  transportadora: null,
  moeda: Moeda.keys.BRL.key,
  dataCompra: formatToCode(moment()),
  dataFaturamento: formatToCode(moment()),
  dataPrevisaoEntrega: null,
  arquivos: [],
  formaPagamento: null,
  prazoPagamento: null,
  modoPagamento: null,
  finalidadeCompra: FinalidadeCompra.keys.USO_CONSUMO.key,
  saving: 0,
  frete: initialStateFrete(),
  centroCusto: null,
  projeto: null,
  mensagem: null,
  observacao: null,
  notificaFornecedor: false,
  mensagensPersonalizadas: [],
  dataNecessidadeEntregaSolicitacao: null,
  tipoNotaEntrada: null,
  cfop: null,
  variacaoCfop: null,
  valorMoedaConvertido: initialStateValoresConvertido(),
  origemCompra: OrigemCompra.keys.CADASTRO.key,
  cadastrosAdicionais: initialStateCamposErp(tipoErp),
  version: 0,
  produtos: [
    initialStateProduto(tipoErp),
  ],
});

export const computeInitialStateProduto = (produto, tipoErp) => {
  if (!produto) {
    return initialStateProduto(tipoErp);
  }

  const computedProduto = { ...initialStateProduto(tipoErp) };

  Object.keys(computedProduto)
    .filter(key => !TEMP_KEYS.includes(key))
    .forEach((key) => {
      computedProduto[key] = produto[key];
    });

  computedProduto.id = produto.id ? produto.id : undefined;

  if (isEmpty(computedProduto.arquivos)) {
    computedProduto.arquivos = produto.produto?.arquivos ?? [];
  }

  return computedProduto;
};

export const computeInitialStateCopiaProduto = (produto, tipoErp) => {
  const computedProduto = { ...initialStateProduto(tipoErp) };

  Object.keys(computedProduto)
    .filter(key => !TEMP_KEYS.includes(key))
    .forEach((key) => {
      computedProduto[key] = produto[key];
    });

  computedProduto.dataPrevisaoEntrega = null;
  computedProduto.dataFaturamento = null;
  computedProduto.especificacaoProduto = getEspecificacaoProduto(produto);
  computedProduto.cadastrosAdicionais = computeFromProduto(tipoErp, produto.cadastrosAdicionais);

  if (isEmpty(computedProduto.arquivos)) {
    computedProduto.arquivos = produto.produto.arquivos ?? [];
  }

  return computedProduto;
};

export const computeInitialStateCopiaAprovacaoProduto = (produto, tipoErp) => {
  const computedProduto = { ...initialStateProduto(tipoErp) };

  Object.keys(computedProduto)
    .filter(key => !TEMP_KEYS.includes(key))
    .forEach((key) => {
      computedProduto[key] = produto[key];
    });

  computedProduto.requisicaoProduto = produto.requisicaoProduto ?? undefined;
  computedProduto.respostaCotacaoProduto = produto.respostaCotacaoProduto ?? undefined;
  computedProduto.especificacaoProduto = getEspecificacaoProduto(produto);
  computedProduto.cadastrosAdicionais = computeFromProduto(tipoErp, produto.cadastrosAdicionais);

  if (isEmpty(computedProduto.arquivos)) {
    computedProduto.arquivos = produto.produto.arquivos ?? [];
  }

  return computedProduto;
};

export const copyProduto = (produto, tipoErp) => {
  const computedProduto = { ...initialStateProduto(tipoErp) };

  Object.keys(computedProduto)
    .filter(key => !TEMP_KEYS.includes(key))
    .forEach((key) => {
      computedProduto[key] = produto[key];
    });

  computedProduto.id = produto.id ?? undefined;
  computedProduto.especificacaoProdutoOrigem = produto.especificacaoProdutoOrigem ?? undefined;
  computedProduto.requisicaoProduto = produto.requisicaoProduto ?? undefined;
  computedProduto.respostaCotacaoProduto = produto.respostaCotacaoProduto ?? undefined;
  computedProduto.situacao = produto.situacao ?? undefined;
  computedProduto.cadastrosAdicionais = computeFromProduto(tipoErp, produto.cadastrosAdicionais);

  if (isEmpty(computedProduto.arquivos)) {
    computedProduto.arquivos = produto.produto?.arquivos ?? [];
  }
  return computedProduto;
};

export const computeInitialCopia = (data, tipoErp) => {
  const copia = { ...initialState(tipoErp) };

  Object.keys(copia)
    .forEach((key) => {
      copia[key] = data[key];
    });

  copia.dataCompra = formatToCode(moment());
  copia.dataFaturamento = null;
  copia.dataPrevisaoEntrega = null;
  copia.dataNecessidadeEntregaSolicitacao = null;
  copia.mensagem = null;
  copia.mensagemFornecedor = null;
  copia.origemCompra = OrigemCompra.keys.CADASTRO.key;
  copia.finalidadeCompra = copia.finalidadeCompra ?? FinalidadeCompra.keys.USO_CONSUMO.key;
  copia.fornecedor = buildFornecedorCompra(data);
  copia.transportadora = buildTransportadoraCompra(data);
  copia.cadastrosAdicionais = computeFrom(tipoErp, data.cadastrosAdicionais);
  copia.produtos = copia.produtos.map(p => ({
    ...computeInitialStateCopiaProduto(p, tipoErp),
  }));

  return copia;
};

export const computeInitialCopiaAprovacao = (data, tipoErp) => {
  const copia = { ...initialState(tipoErp) };

  Object.keys(copia)
    .forEach((key) => {
      copia[key] = data[key];
    });

  copia.origemCompra = OrigemCompra.keys.CADASTRO.key;
  copia.fornecedor = buildFornecedorCompra(data);
  copia.transportadora = buildTransportadoraCompra(data);
  copia.cadastrosAdicionais = computeFrom(tipoErp, data.cadastrosAdicionais);

  copia.produtos = copia.produtos.map(p => ({
    ...computeInitialStateCopiaAprovacaoProduto(p, tipoErp),
  }));

  return copia;
};

export const computeInitialRascunho = (data, tipoErp) => {
  const rascunho = { ...initialState(tipoErp) };

  Object.keys(rascunho)
    .forEach((key) => {
      rascunho[key] = data[key];
    });

  rascunho.id = data.id;
  rascunho.fornecedor = buildFornecedorCompra(data);
  rascunho.transportadora = buildTransportadoraCompra(data);

  rascunho.cadastrosAdicionais = computeFrom(tipoErp, data.cadastrosAdicionais);

  if (!isEmpty(rascunho.produtos)) {
    rascunho.produtos = rascunho.produtos.map(p => ({
      ...copyProduto(p, tipoErp),
    }));
  } else {
    rascunho.produtos = [initialStateProduto(tipoErp, data)];
  }

  return rascunho;
};

export const computeInitialEdicao = (data, tipoErp) => {
  const edicao = {};

  Object.keys(initialState(tipoErp))
    .forEach((key) => {
      edicao[key] = data[key];
    });

  edicao.id = data.id;
  edicao.fornecedor = buildFornecedorCompra(data);
  edicao.transportadora = buildTransportadoraCompra(data);
  edicao.cadastrosAdicionais = computeFrom(tipoErp, data.cadastrosAdicionais);
  edicao.codigoInterno = data.codigoInterno ?? undefined;

  edicao.produtos = edicao.produtos.map(p => ({
    ...copyProduto(p, tipoErp),
  }));

  return edicao;
};

const computeInitialStateRequisicaoCompraProduto = (
  produto,
  tipoErp,
  data,
) => {
  const computedProduto = { ...initialStateProduto(tipoErp) };

  Object.keys(computedProduto)
    .filter(key => !TEMP_KEYS.includes(key))
    .forEach((key) => {
      computedProduto[key] = produto[key];
    });

  computedProduto.especificacaoProdutoOrigem = produto.especificacaoProdutoOrigem ?? undefined;
  computedProduto.requisicaoProduto = produto.requisicaoProduto ?? undefined;
  computedProduto.impostos = produto.impostos ?? initialStateImpostos();
  computedProduto.dataFaturamento = data.dataFaturamento ?? null;
  computedProduto.finalidadeCompra = produto.finalidadeCompra ?? data.finalidadeCompra;
  computedProduto.dataPrevisaoEntrega = getValidDate(
    produto.dataPrevisaoEntrega,
    data.dataPrevisaoEntrega,
  );

  computedProduto.cadastrosAdicionais = computeFromProduto(tipoErp, produto.cadastrosAdicionais);

  if (isEmpty(computedProduto.arquivos)) {
    computedProduto.arquivos = produto.produto?.arquivos ?? [];
  }
  return computedProduto;
};

export const computeInitialRequisicaoCompra = (data, tipoErp) => {
  const dataPrevisaoEntregaValida = getValidDate(data.dataPrevisaoEntrega);
  const requisicao = { ...initialState(tipoErp) };

  Object.keys(initialState(tipoErp))
    .forEach((key) => {
      requisicao[key] = data[key];
    });

  requisicao.dataPrevisaoEntrega = dataPrevisaoEntregaValida;
  requisicao.dataCompra = formatToCode(moment());
  requisicao.dataFaturamento = formatToCode(moment());
  requisicao.frete.totalTransporteConvertido = requisicao.frete.totalTransporte;
  const validProdutos = requisicao.produtos
    .filter(produto => checkIsProdutoValido(produto.requisicaoProduto.situacao))
    .map(produto => ({
      ...computeInitialStateRequisicaoCompraProduto(produto, tipoErp, requisicao),
    }));

  requisicao.produtos = orderBy(validProdutos, ['requisicaoProduto.id'], ['asc']);

  requisicao.cadastrosAdicionais = computeFrom(tipoErp, data.cadastrosAdicionais);

  return requisicao;
};

export default {
  initialState,
  initialStateProduto,
};
